/**
 * Share Certificate (standalone, M32205)
 * Transfer 패키지 Stage 4 화면을 단독 문서로 분리.
 */
export type ShareCertFieldType = 'text' | 'date' | 'entity' | 'person' | 'address';

export const SHARE_CERT_KEYS = [
  'cert_no',
  'no_of_shares',
  'shares_numeral',
  'shares_words',
  'share_class',
  'member_name',
  'company_name',
  'company_registration_no',
  'registered_office_address',
  'issue_date',
] as const;

export type ShareCertFieldKey = (typeof SHARE_CERT_KEYS)[number];

const LABELS: Record<string, string> = {
  cert_no: 'Certificate No.',
  no_of_shares: 'No. of Shares',
  shares_numeral: 'No. of Shares (figures)',
  shares_words: 'No. of Shares (words)',
  share_class: 'Class of Shares',
  member_name: 'Registered Holder',
  company_name: 'Company Name',
  company_registration_no: 'Company Registration No.',
  registered_office_address: 'Registered Office Address',
  issue_date: 'Issue Date',
};

export function labelize(key: string): string {
  return LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function typeOf(key: string): ShareCertFieldType {
  if (key === 'issue_date' || /_date$/.test(key)) return 'date';
  if (/_address$/.test(key)) return 'address';
  if (key === 'member_name') return 'person';
  if (key === 'company_name') return 'entity';
  return 'text';
}

export function isOptional(key: string): boolean {
  return key === 'shares_words' || key === 'shares_numeral';
}

/** no_of_shares 변경 시 shares_numeral / shares_words 는 패널이 자동 동기화 */
export const SHARE_CERT_REQUIRED = SHARE_CERT_KEYS.filter(
  (k) => !isOptional(k) && k !== 'shares_numeral' && k !== 'shares_words',
);

/** 정수(주식수) → 영문 단어 (Transfer Share Certificate 와 동일). */
const _WONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const _WTENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const _WSCALES = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];
function _three(n: number): string {
  const out: string[] = [];
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h > 0) out.push(_WONES[h], 'Hundred');
  if (r > 0) {
    out.push(r < 20 ? _WONES[r] : (`${_WTENS[Math.floor(r / 10)]}${r % 10 ? ` ${_WONES[r % 10]}` : ''}`));
  }
  return out.join(' ').trim();
}
export function sharesToWords(v: any): string {
  let n = Math.floor(Number(String(v ?? '').replace(/[^0-9]/g, '')) || 0);
  if (n <= 0) return '';
  const groups: number[] = [];
  while (n > 0) {
    groups.push(n % 1000);
    n = Math.floor(n / 1000);
  }
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    if (groups[i] === 0) continue;
    parts.push(`${_three(groups[i])}${_WSCALES[i] ? ` ${_WSCALES[i]}` : ''}`);
  }
  return parts.join(' ').trim();
}
